import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Card,
  Group,
  Text,
  SegmentedControl,
  TextInput,
  Flex,
} from "@mantine/core";

import { PaginatedList } from "../shared";

type UserScopeFilter = "entire" | "active" | "created" | "deleted";
type JoinTypeFilter = "entire" | "organization_user" | "organization_admin";

interface UserListFilters {
  scope: UserScopeFilter;
  search_value: string;
  join_type: JoinTypeFilter;
}

interface UserRecord {
  id: number | string;
  first_name?: string;
  last_name?: string;
  logged_in?: boolean;
  status?: number;
  email?: string;
  company_name?: string;
  [key: string]: unknown;
}

interface RequestInfo {
  url: string;
  data: {
    organization_id?: number | string;
    [key: string]: unknown;
  };
}

interface UserListProps {
  items?: unknown[];
  requestInfo?: RequestInfo;
  isIndex?: boolean;
}

export default function UserList({
  items = [],
  requestInfo = {
    url: "/retrieve-users/",
    data: {},
  },
  isIndex: _isIndex,
}: UserListProps) {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<UserListFilters>({
    scope: "entire",
    search_value: "",
    join_type: "organization_user",
  });

  const reqData = {
    ...requestInfo.data,
    ...filters,
  };

  return (
    <div>
      {requestInfo && (
        <React.Fragment>
          <TextInput
            placeholder={String(t("search"))}
            size="lg"
            value={filters.search_value}
            onChange={(e) =>
              setFilters({ ...filters, search_value: e.target.value })
            }
          />
          <Flex mt="sm" gap="sm" direction={{ base: "column", sm: "row" }}>
            <SegmentedControl
              data={[
                { label: String(t("all")), value: "entire" },
                { label: String(t("active")), value: "active" },
                { label: String(t("created")), value: "created" },
                { label: String(t("disabled")), value: "deleted" },
              ]}
              value={filters.scope}
              onChange={(value) =>
                setFilters({ ...filters, scope: value as UserScopeFilter })
              }
              size="xs"
            />
            {requestInfo.data.organization_id && (
              <SegmentedControl
                data={[
                  { label: String(t("all")), value: "entire" },
                  { label: "User", value: "organization_user" },
                  { label: "Admin", value: "organization_admin" },
                ]}
                value={filters.join_type}
                onChange={(value) =>
                  setFilters({ ...filters, join_type: value as JoinTypeFilter })
                }
                size="xs"
              />
            )}
          </Flex>
        </React.Fragment>
      )}
      <PaginatedList
        tableData={items as never[]}
        requestInfo={{
          url: requestInfo.url,
          data: reqData,
        }}
        responseMapping={(r: UserRecord[]) =>
          r.map((m) => ({
            ...m,
            link_url: `/users/${m.id}`,
          }))
        }
        displayFormatter={(item: { original: UserRecord }) => {
          return (
            <Card key={item.original.id}>
              <Group>
                <Text
                  c="dark"
                  fw={700}
                  component={Link}
                  to={`/users/${item.original.id}`}
                >
                  {item.original.first_name} {item.original.last_name}
                </Text>
              </Group>
              <Text size="sm">{item.original.email}</Text>
            </Card>
          );
        }}
      />
    </div>
  );
}
